<template>
  <div class="row">

    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-alert_fehler" aria-hidden="true"></span>
        {{ $t('views.blacklist.title') }}
      </h1>
    </div>

    <link-back to="/" i18nMessage="common.backToDashboard" class="mt-2 hide-in-print" />

    <loading-container :state="state" errorLinkTo="/">

      <link-add-element i18nMessage="common.blacklist.add" :to="{ name: 'admin-blacklist-add' }" class="d-flex justify-content-end mb-2" />

      <div class="row">
        <div class="col-12 col-md-8">
          <control-search i18nLabel="views.reservations.search" v-model="searchTerm" v-on:keyup.enter="loadFromServer()" />
        </div>
        <div class="col-12 col-md-2">
          <button-search v-on:clicked="loadFromServer()" :labelHidden="true" />
        </div>
        <div class="col-12 col-md-2">
          <button-reset v-on:clicked="resetTerm()" v-if="isResettable()" :labelHidden="true" class="m-0 p-0 btn-to-danger-link-filter" />
        </div>
      </div>

      <table v-if="blacklist.length > 0" class="table table-striped my-4 table-fixed">
        <thead class="thead-dark">
          <tr>
            <th class="p-0">
              <button @click.prevent="doSort('email')" class="btn btn-table-sort">
              <span>{{ $t('common.blacklist.email') }}</span>
              <span class="icon icon-navigation_pfeil_unten" aria-hidden="true" v-if="sortBy ==='email' && sortOrder==='asc'"></span>
              <span class="icon icon-navigation_pfeil_oben" aria-hidden="true" v-if="sortBy ==='email' && sortOrder==='desc'"></span>
              <span class="icon icon-filter" aria-hidden="true" v-if="sortBy !=='email'"></span>
              </button>
            </th>
            <th class="p-0">
              <button @click.prevent="doSort('expiredAt')" class="btn btn-table-sort">
              <span>{{ $t('common.blacklist.expiredAt') }}</span>
              <span class="icon icon-navigation_pfeil_unten" aria-hidden="true" v-if="sortBy ==='expiredAt' && sortOrder==='asc'"></span>
              <span class="icon icon-navigation_pfeil_oben" aria-hidden="true" v-if="sortBy ==='expiredAt' && sortOrder==='desc'"></span>
              <span class="icon icon-filter" aria-hidden="true" v-if="sortBy !=='expiredAt'"></span>
              </button>
            </th>
            <th class="p-0">
              <button @click.prevent="doSort('description')" class="btn btn-table-sort">
              <span>{{ $t('common.blacklist.description') }}</span>
              <span class="icon icon-navigation_pfeil_unten" aria-hidden="true" v-if="sortBy ==='description' && sortOrder==='asc'"></span>
              <span class="icon icon-navigation_pfeil_oben" aria-hidden="true" v-if="sortBy ==='description' && sortOrder==='desc'"></span>
              <span class="icon icon-filter" aria-hidden="true" v-if="sortBy !=='description'"></span>
              </button>
            </th>
            <th class="table-width-100">{{ $t('common.blacklist.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(blacklistEntry, index) in blacklist" :key="index">
            <td>{{ blacklistEntry.email }}</td>
            <td>{{ formatDate(blacklistEntry) }}</td>
            <td>{{ blacklistEntry.description }}</td>
            <td class="hide-in-print table-width-100">
              <div class="d-flex justify-content-center aling-items-center">
                <link-default i18nMessage="common.blacklist.edit" :to="{ name: 'admin-blacklist-edit', params: { email: blacklistEntry.email }, query: { 'fromSearch': 'y' } }" :iconOnly="true" iconClass="icon-editieren" class="align-self-center" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <feedback-message v-if="blacklist.length < 1" :message="$t('common.blacklist.noEntry')" />
    </loading-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import LoadingMixin from '@/mixins/LoadingMixin'

import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import { formatDateString } from '@/utils/formatUtils';
import LinkDefault from '@/components/interaction/LinkDefault.vue';
import LinkAddElement from '@/components/interaction/LinkAddElement.vue';
import { adminService } from '@/services/adminService';
import { LoadingState } from '@/utils/loadingUtils';
import ControlSearch from '@/components/controls/ControlSearch.vue';
import ButtonSearch from '@/components/interaction/ButtonSearch.vue';
import { BlacklistEntry } from '@/generated/openapi';
import FeedbackMessage from '@/components/feedback/FeedbackMessage.vue';
import LinkBack from '@/components/interaction/LinkBack.vue';
import ButtonReset from '@/components/interaction/ButtonReset.vue';

export default defineComponent({
  name: 'Blacklist',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      blacklist: [] as BlacklistEntry[],
      searchTerm: '',
      sortBy: 'email',
      sortOrder: 'asc',
    }
  },
  created() {
    this.loadFromServer()
  },
  methods: {
    doSort(sortBy: string) {
      if (this.sortBy === sortBy && this.sortOrder === 'asc') {
        this.sortOrder = 'desc'
      } else {
        this.sortOrder = 'asc'
      }
      this.sortBy = sortBy
      this.loadFromServer()
    },
    loadFromServer() {
      this.state = LoadingState.loading
      adminService.getBlackList(this.searchTerm, this.sortBy, this.sortOrder)
        .then(blacklist => {
          this.blacklist = blacklist
          this.state = LoadingState.success
        }).catch(this.handleError)
    },
    formatDate(blacklistEntry: BlacklistEntry) {
      return formatDateString(blacklistEntry.expiredAt)
    },
    isResettable() {
      return !!(this.searchTerm)
    },
    resetTerm(){
      this.searchTerm = ''
      this.loadFromServer()
    }
  },
  components: {
    LoadingContainer,
    LinkDefault,
    LinkAddElement,
    ControlSearch,
    ButtonSearch,
    FeedbackMessage,
    LinkBack,
    ButtonReset
  }
});
</script>
